import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchAuthSession, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import { useIdleTimer } from 'react-idle-timer';
import { useLocation, useNavigate } from 'react-router';

import {
  BETTERFLY_DOMAIN,
  DEEPLINK_URL_PARAM,
  GOBETTERFLY_DOMAIN,
  LOGIN_PROVIDER_BROKERS_PORTAL,
  LOGIN_PROVIDER_NAME,
  LOGIN_ROUTE,
  PROSPECTS_ROUTE,
  SESSION_INACTIVITY_TIMEOUT
} from '../../constants/auth';
import {
  CLICK_ACTION,
  BROKERS_PORTAL_PROVIDER,
  IDENTIFY_EVENT,
  LOGIN,
  LOGOUT,
  SET_ACTION,
  STATE_ERROR,
  STATE_SUCCESSFUL,
  USER_ATTRIBUTES_IDENTIFIER
} from '../../constants/tagManager';
import { clearAllStoredCensusEntries } from '../../hooks/useActivationCensusFlow/activationCensus';
import { AuthContext } from '../../hooks/useAuth/useAuth';
import TagManagerService from '../../services/gtm/tagManagerService';
import {
  getAndRemoveLocalStorage,
  getFromLocalStorage,
  removeFromLocalStorage,
  toLocalStorage
} from '../../services/storage/storage';

const AuthContextProvider = ({ children }: React.PropsWithChildren) => {
  const [userId, setUserId] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [verifyingLogin, setVerifyingLogin] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [isBetterflyer, setIsBetterflyer] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const extractUserFromSession = async (): Promise<{ userId: string; email: string } | null> => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (idToken && typeof idToken === 'object' && idToken.payload) {
      const p = idToken.payload;
      const getString = (val: unknown) => (typeof val === 'string' ? val : '');
      return {
        userId: getString(p.sub),
        email: getString(p.email)
      };
    }
    return null;
  };

  const redirectToApp = useCallback((): void => {
    const redirectionPath = getFromLocalStorage(DEEPLINK_URL_PARAM);
    removeFromLocalStorage(DEEPLINK_URL_PARAM);

    const isValidPath =
      redirectionPath &&
      typeof redirectionPath === 'string' &&
      redirectionPath.startsWith('/') &&
      !redirectionPath.startsWith('//') &&
      !/^https?:\/\//i.test(redirectionPath);

    const targetPath = isValidPath ? redirectionPath : PROSPECTS_ROUTE;
    navigate(targetPath, { replace: true });
  }, [navigate]);

  const sessionLogin = useCallback(
    async (userEmail: string, password: string, recaptchaToken: string) => {
      try {
        const sessionData = await signIn({
          username: userEmail,
          password,
          options: { clientMetadata: { recaptchaToken } }
        });
        if (sessionData.isSignedIn) {
          const userData = await extractUserFromSession();
          if (userData) {
            setUserId(userData.userId);
            setEmail(userData.email);
          }
          setIsLoggedIn(true);
          setErrorCode('');
          setIsBetterflyer(userEmail.endsWith(BETTERFLY_DOMAIN) || userEmail.endsWith(GOBETTERFLY_DOMAIN));
          toLocalStorage(LOGIN_PROVIDER_NAME, LOGIN_PROVIDER_BROKERS_PORTAL);

          TagManagerService.pushEvent(CLICK_ACTION, LOGIN, { ...STATE_SUCCESSFUL, provider: BROKERS_PORTAL_PROVIDER });
          if (userData?.userId) {
            TagManagerService.pushEvent(SET_ACTION, IDENTIFY_EVENT, {
              identifier: USER_ATTRIBUTES_IDENTIFIER.concat(userData.userId)
            });
          }

          redirectToApp();
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        setErrorCode(errorMessage);
        TagManagerService.pushEvent(CLICK_ACTION, LOGIN, STATE_ERROR);
      }
    },
    [redirectToApp]
  );

  const sessionLogout = useCallback(async () => {
    try {
      const provider = getAndRemoveLocalStorage(LOGIN_PROVIDER_NAME) ?? LOGIN_PROVIDER_BROKERS_PORTAL;
      setErrorCode('');

      TagManagerService.pushEvent(CLICK_ACTION, LOGOUT, STATE_SUCCESSFUL);
      await signOut();

      setUserId('');
      setEmail('');
      setIsLoggedIn(false);
      setIsBetterflyer(false);
      removeFromLocalStorage(DEEPLINK_URL_PARAM);
      clearAllStoredCensusEntries();

      if (provider === LOGIN_PROVIDER_BROKERS_PORTAL) {
        navigate(LOGIN_ROUTE);
      }
    } catch {
      TagManagerService.pushEvent(CLICK_ACTION, LOGOUT, STATE_ERROR);
    }
  }, [navigate]);

  const resetError = useCallback((err: string) => {
    setErrorCode(err);
  }, []);

  const socialLogin = useCallback(async ({ email = '', sub = '' }: { email?: string; sub?: string }) => {
    // Implementation will be added when SSO is needed
    console.warn('Social login not yet implemented', { email, sub });
  }, []);

  const customLogin = useCallback(
    async (token: { attributes: { email?: string; sub?: string } }, provider: string, gtmProvider: string) => {
      // Implementation will be added when SSO is needed
      console.warn('Custom login not yet implemented', { token, provider, gtmProvider });
    },
    []
  );

  const handleOnIdle = useCallback(() => {
    if (isLoggedIn) {
      sessionLogout();
    }
  }, [isLoggedIn, sessionLogout]);

  useIdleTimer({
    timeout: SESSION_INACTIVITY_TIMEOUT,
    onIdle: handleOnIdle
  });

  useEffect(() => {
    async function checkSession() {
      setVerifyingLogin(true);
      try {
        await getCurrentUser();
        const userData = await extractUserFromSession();
        if (userData) {
          setUserId(userData.userId);
          setEmail(userData.email);
          setIsBetterflyer(userData.email.endsWith(BETTERFLY_DOMAIN) || userData.email.endsWith(GOBETTERFLY_DOMAIN));
        }
        setIsLoggedIn(true);
        removeFromLocalStorage(DEEPLINK_URL_PARAM);

        if (location.pathname === LOGIN_ROUTE) {
          navigate(PROSPECTS_ROUTE, { replace: true });
        }
      } catch {
        removeFromLocalStorage(LOGIN_PROVIDER_NAME);
        setUserId('');
        setEmail('');
        setIsLoggedIn(false);
        setIsBetterflyer(false);
      } finally {
        setVerifyingLogin(false);
      }
    }

    checkSession();
  }, [navigate, location]);

  const value = useMemo(
    () => ({
      userId,
      email,
      isLoggedIn,
      verifyingLogin,
      errorCode,
      isBetterflyer,
      sessionLogin,
      sessionLogout,
      resetError,
      socialLogin,
      customLogin
    }),
    [
      userId,
      email,
      isLoggedIn,
      verifyingLogin,
      errorCode,
      isBetterflyer,
      sessionLogin,
      sessionLogout,
      resetError,
      socialLogin,
      customLogin
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
